// http.ts - Axios instance with JWT interceptors and refresh logic (TypeScript)
import axios, {
  type AxiosInstance,
  type AxiosError,
  type InternalAxiosRequestConfig,
} from "axios";
import type { AuthResponse } from "../types";

// Base URL: Vite env (import.meta.env) or default
// NOTE: Use server root without /v1 so we can call both /v1/... and unversioned endpoints like /me/addresses
const BASE_URL =
  (import.meta && import.meta.env && import.meta.env.VITE_API_BASE_URL) ||
  "https://server.shop.co.zm/lyt-store";

function hasNonAscii(value: string): boolean {
  return /[^\x00-\x7F]/.test(value);
}

function assertUtf8SafePayload(payload: unknown, context: string = "request"): void {
  const visited = new WeakSet();

  const walk = (val: unknown, keyPath: string) => {
    if (val === null || val === undefined) return;
    if (typeof val === "string") {
      if (hasNonAscii(val)) {
        const pathInfo = keyPath ? ` at "${keyPath}"` : "";
        const err: NormalizedError = {
          status: null,
          message: `Remove special characters${pathInfo}; only ASCII characters are allowed in ${context}.`,
        };
        throw err;
      }
      return;
    }
    if (typeof val !== "object") return;
    if (visited.has(val as object)) return;
    visited.add(val as object);
    if (Array.isArray(val)) {
      val.forEach((item, idx) => walk(item, `${keyPath}[${idx}]`));
      return;
    }
    // Skip binary payloads/files
    if (val instanceof File || val instanceof Blob) return;
    Object.entries(val as Record<string, unknown>).forEach(([k, v]) => {
      const nextPath = keyPath ? `${keyPath}.${k}` : k;
      walk(v, nextPath);
    });
  };

  walk(payload, "");
}

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

const processQueue = (
  error: Error | null = null,
  token: string | null = null
): void => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// Single axios instance with credentials disabled for all requests
// This allows public endpoints to work without CORS errors while still
// supporting JWT authentication via Authorization header

const http: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  //timeout: 20000,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: false,
  // Configure params serializer for proper array handling with Spring Boot
  // Spring Boot expects multiple parameters with the same name for arrays
  paramsSerializer: {
    serialize: (
      params: Record<
        string,
        string | number | boolean | (string | number | boolean)[]
      >
    ): string => {
      // Convert arrays to multiple parameters with the same name
      const searchParams = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        if (Array.isArray(value)) {
          // Add each array item as a separate parameter with the same name
          value.forEach((item) => {
            searchParams.append(key, String(item));
          });
        } else if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      }
      return searchParams.toString();
    },
  },
});

// Token helpers
const tokenKey = "lyt_token";
const refreshKey = "lyt_refresh_token";
const cartSessionKey = "lyt_cart_session";

export interface AuthTokens {
  accessToken: string;
  refreshToken?: string;
}

export function getAccessToken(): string | null {
  return localStorage.getItem(tokenKey);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(refreshKey);
}

export function setAuthTokens({ accessToken, refreshToken }: AuthTokens): void {
  if (accessToken) localStorage.setItem(tokenKey, accessToken);
  if (refreshToken) localStorage.setItem(refreshKey, refreshToken);
}

export function clearAuthTokens(): void {
  localStorage.removeItem(tokenKey);
  localStorage.removeItem(refreshKey);
}

function getCartSessionId(): string {
  const existing = localStorage.getItem(cartSessionKey);
  if (existing) return existing;
  const next = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  localStorage.setItem(cartSessionKey, next);
  return next;
}

// Attach Authorization header to requests
http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Ensure cart session id is consistently sent for cart endpoints to keep cookies aligned
  const url = config.url || "";
  const isCartRequest = url.includes("/cart");
  if (isCartRequest) {
    config.headers = config.headers || {};
    (config.headers as Record<string, string>)["X-Cart-Session"] = getCartSessionId();
  }

   // Validate payload for ASCII-only input to avoid non-UTF8 submissions
  try {
    if (config.data) {
      assertUtf8SafePayload(config.data, "request body");
    }
    if (config.params) {
      assertUtf8SafePayload(config.params, "request params");
    }
  } catch (validationError: any) {
    return Promise.reject(validationError);
  }

  return config;
});

// Custom error interface for normalized errors
export interface NormalizedError {
  status: number | null;
  url?: string;
  method?: string;
  message: string;
  data?: unknown;
  code?: string;
  requiresLogin?: boolean;
}

// Response interceptor with refresh flow and quick login
http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };
    const status = error.response ? error.response.status : null;

    // Only attempt auth handling if we actually sent a token
    const hadToken = !!originalRequest?.headers?.Authorization;

    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      hadToken
    ) {
      originalRequest._retry = true;

      const refreshToken = getRefreshToken();
      const isAuthEndpoint = originalRequest.url?.includes("/v1/auth/");

      // If we have a refresh token, try to refresh
      if (refreshToken && !isAuthEndpoint) {
        if (isRefreshing) {
          return new Promise<string>((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return http(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        isRefreshing = true;

        try {
          // Refresh endpoint lives under /v1
          const { data } = await http.post<AuthResponse>(
            `${BASE_URL}/v1/auth/refresh`,
            { refreshToken }
          );
          const newAccess = data?.accessToken;
          const newRefresh = data?.refreshToken || refreshToken;
          if (!newAccess)
            throw new Error("No access token in refresh response");

          setAuthTokens({ accessToken: newAccess, refreshToken: newRefresh });
          http.defaults.headers.common.Authorization = `Bearer ${newAccess}`;
          processQueue(null, newAccess);
          return http(originalRequest);
        } catch (refreshErr) {
          processQueue(
            refreshErr instanceof Error
              ? refreshErr
              : new Error("Refresh failed"),
            null
          );
          clearAuthTokens();
        } finally {
          isRefreshing = false;
        }
      }

      // No refresh token or refresh failed - reject with special error
      // The UI components will handle showing the QuickLoginDialog
      const authError: NormalizedError = {
        status: 401,
        code: "AUTH_REQUIRED",
        message: "Authentication required",
        url: originalRequest.url,
        method: originalRequest.method,
        requiresLogin: true,
      };
      return Promise.reject(authError);
    }

    // Normalize error
    const errorData = error.response?.data as {
      message?: string;
      error?: { message?: string; errorCode?: string };
    } | undefined;
    const normalized: NormalizedError = {
      status,
      url: originalRequest?.url,
      method: originalRequest?.method,
      message: errorData?.error?.message || errorData?.message || error.message || "Request failed",
      code: errorData?.error?.errorCode,
      data: error.response?.data,
    };
    return Promise.reject(normalized);
  }
);

// Export the single http instance
export default http;
